import React from 'react';
import { Box } from '@mui/material';
import { AlertBox } from '../AlertBox';
import { AlertBoxMessage } from '../AlertBox/AlertBoxMessage';
import { AlertBoxTitle } from '../AlertBox/AlertBoxTitle';
import { useMaintenanceCopy } from './useMaintenanceCopy';

export { useMaintenanceCopy, MAINTENANCE_MODE_FLAG } from './useMaintenanceCopy';
export type { MaintenanceCopy } from './useMaintenanceCopy';

export interface MaintenanceBannerProps {
  /** Optional margin applied to the underlying AlertBox. */
  margin?: string;
}

/**
 * Banner shown while the global `maintenance-mode` release flag is on, telling
 * the user the platform is temporarily read-only.
 *
 * Sits alongside — and reads differently from — COM-112's
 * {@link DegradedServiceBanner}: that one warns of degradation with everything
 * still working, so it is a `warning`; this one reports a deliberate, bounded
 * window in which reads work and writes do not, so it is an `info`.
 *
 * Two acceptance criteria fall out of `usePlatformFlags` for free: it polls
 * `/api/v1/static/flags` every 60s, so the state arrives **proactively** rather
 * than after the user has already hit a failed request; and it retains the
 * last-known flags on error, so an unreachable flags endpoint reads as
 * maintenance-off rather than as a spurious window.
 *
 * When a write has actually been refused, the recorded refusal (recognised from
 * the typed `MAINTENANCE` 503) is explained here too, because write controls
 * stay enabled during a window by deliberate decision — see the 11 Aug 2026
 * decision on NS-2461. The banner is the persistent account of the window;
 * {@link MaintenanceRefusalToast} is what tells the user at the moment their
 * write is refused, since they may be nowhere near the top of the page.
 *
 * Renders nothing when the flag is off or absent. Copy comes from
 * {@link useMaintenanceCopy}, which is shared with the toast and the webchat
 * error so the three cannot drift apart.
 *
 * Accessibility: the underlying `AlertBox` carries `role="alert"`, so the state
 * and the refusal reason are announced to a screen-reader user rather than only
 * appearing visually.
 */
export const MaintenanceBanner: React.FC<MaintenanceBannerProps> = ({ margin = '8px 8px 0' }) => {
  const { isMaintenanceMode, refusal, title, message, refusalMessage, clearRefusal } = useMaintenanceCopy();

  const wasMaintenanceMode = React.useRef(false);

  React.useEffect(() => {
    // A refusal belongs to the window it happened in. Drop it when the flag
    // actually transitions off, so a later window cannot open still showing the
    // previous one's explanation. Keyed on the transition rather than on the
    // flag being falsy, so a refusal recorded before the first poll lands is not
    // discarded.
    if (wasMaintenanceMode.current && !isMaintenanceMode) clearRefusal();
    wasMaintenanceMode.current = isMaintenanceMode;
  }, [isMaintenanceMode, clearRefusal]);

  if (!isMaintenanceMode) return null;

  return (
    <Box data-testid="maintenance-banner">
      <AlertBox alertType="info" alertBoxMargin={margin}>
        <AlertBoxTitle disableMarginTop>{title}</AlertBoxTitle>
        <AlertBoxMessage>{message}</AlertBoxMessage>
        {refusal && (
          <Box data-testid="maintenance-banner-refusal">
            <AlertBoxMessage>{refusalMessage}</AlertBoxMessage>
          </Box>
        )}
      </AlertBox>
    </Box>
  );
};
